import React from 'react';

/**
 * UserTabs — the five profile surfaces.
 *
 * These are five separate server-rendered routes in the legacy app
 * (`views/users/tpl-users-menu.twig`), not client-side tabs, and this MVP
 * keeps that: only `/users/edit/` is React so far, and the remaining links
 * still land on the AngularJS pages. Rendering them as anchors — rather than
 * as state — means the tab bar is already correct when the other four are
 * migrated; only the `href` values change.
 *
 * @param {Object} props
 * @param {string} props.base      URL prefix (`/org/{unique}` or `''`).
 * @param {string} props.suffix    Trailing path segment (`/{userId}/` or `/`).
 * @param {string} props.active    Key of the current tab.
 */
/**
 * Documents, Certificates and Endorsements were three tabs; the owner asked for
 * one. They collapse into `credentials`, whose landing surface is Documents —
 * `CredentialsSubNav` moves between the three underneath.
 */
const TABS = [
	{ key: 'edit', label: 'Personal Information', path: '/users/edit' },
	{ key: 'credentials', label: 'Credentials', path: '/users/documents' },
	{ key: 'settings', label: 'Settings', path: '/usersettings' }
];

/** Which top-level tab a section belongs to. */
const TAB_FOR_SECTION = {
	edit: 'edit',
	documents: 'credentials',
	certificates: 'credentials',
	personalinfo: 'credentials',
	settings: 'settings'
};

function UserTabs({ base = '', suffix = '/', active = 'edit' }) {
	return (
		<nav
			aria-label="Profile sections"
			// `overflow-y-hidden` is not redundant: per CSS, setting one axis to
			// a non-visible value computes the other to `auto`, and the tabs'
			// `-mb-px` sits a pixel past the box — enough for a vertical
			// scrollbar to appear on the strip.
			className="flex gap-1 border-b border-default-200 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
		>
			{TABS.map(t => {
				const isActive = t.key === (TAB_FOR_SECTION[active] || active);
				// Settings is user-scoped in the legacy router — it has no
				// org/element variant, so it never takes the suffix.
				const href = t.key === 'settings' ? t.path : `${base}${t.path}${suffix}`;
				return (
					<a
						key={t.key}
						href={href}
						aria-current={isActive ? 'page' : undefined}
						className={[
							// 44px min touch target below desktop (responsive contract §5).
							'inline-flex items-center h-11 px-4 whitespace-nowrap text-[14px] no-underline',
							'border-b-2 -mb-px transition-colors',
							isActive
								? 'font-semibold text-[hsl(var(--heroui-primary))] border-[hsl(var(--heroui-primary))]'
								: 'font-medium text-default-500 border-transparent hover:text-foreground'
						].join(' ')}
					>
						{t.label}
					</a>
				);
			})}
		</nav>
	);
}

export default UserTabs;
